// The Fleet list's filters, read from and written to the URL query string.
//
// WHY THE URL AND NOT COMPONENT STATE. Platform request #5: an operator filtered
// the list, opened a car, came back, and every filter and the search box had been
// reset. Component state dies with the page; the query string is part of the
// history entry, so the back button brings it back, a refresh keeps it, and a
// filtered list can be pasted to a colleague. sessionStorage was considered and
// rejected: it survives the back button but not a shared link, and it would
// silently reapply yesterday's filters to a page opened fresh from the sidebar.
//
// Imports nothing, so the test runner can load it directly. The valid location
// buckets are passed in rather than imported, because web/src imports have no
// extension and Node's runner needs one.

export type FleetFilters = {
  q: string
  tab: string
  locations: string[]
  activeOnly: boolean
  generalFleetOnly: boolean
}

// Opens on Onboarded, not All. Luyanda, 1 September 2026. Kept as the default so
// a bare /fleet still opens where it always did, and the URL stays clean for the
// common case: the default tab is never written out.
export const DEFAULT_FLEET_TAB = 'active'

// A location the page does not know (a stale link, a typo) is dropped rather
// than applied, because an unknown bucket would match nothing and show an empty
// list with no visible filter to clear.
export function readFleetFilters(
  params: URLSearchParams,
  buckets: readonly string[],
): FleetFilters {
  const locs = (params.get('loc') ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => buckets.includes(s))
  return {
    q: params.get('q') ?? '',
    tab: params.get('tab') || DEFAULT_FLEET_TAB,
    locations: Array.from(new Set(locs)),
    activeOnly: params.get('active') === '1',
    generalFleetOnly: params.get('general') === '1',
  }
}

// Returns a new URLSearchParams with the Fleet keys replaced and every other key
// left alone, so a parameter some other feature put on the URL is not dropped.
// Empty and default values are removed rather than written as blanks.
export function writeFleetFilters(
  current: URLSearchParams,
  patch: Partial<FleetFilters>,
  buckets: readonly string[],
): URLSearchParams {
  const merged = { ...readFleetFilters(current, buckets), ...patch }
  const next = new URLSearchParams(current)
  const set = (k: string, v: string | null) => (v ? next.set(k, v) : next.delete(k))
  set('q', merged.q ? merged.q : null)
  set('tab', merged.tab && merged.tab !== DEFAULT_FLEET_TAB ? merged.tab : null)
  set('loc', merged.locations.length > 0 ? merged.locations.join(',') : null)
  set('active', merged.activeOnly ? '1' : null)
  set('general', merged.generalFleetOnly ? '1' : null)
  return next
}
